import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { getFieldNameFromEqlField } from '@modules/common/utils';
import { getDisplayNameFromField } from '@modules/common/utils/getDisplayNameFromField';
import { CooccurrenceIcon } from '@modules/quantify/components/CooccurrenceIcon';
import { QUANTIFY_COOCCURRING_HELP_DESK_ARTICLE } from '@modules/quantify/constants';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  CompareReportDataPoint,
  QuantifyReportDataPoint,
} from '@modules/quantify/store/types';
import { LabelUpdateInProgressIcon } from '@modules/taxonomy/components/LabelUpdateInProgressIcon';
import { getInfoMessageForLabelState } from '@modules/taxonomy/helpers';
import { OpenInNewRounded } from '@mui/icons-material';
import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { LabelType } from 'generated/graphql';
import Highlighter from 'react-highlight-words';

const COOCCURRENCE_ICON_SIZE = 24;

interface QuantifyReportTableLabelCellProps {
  params: GridRenderCellParams<
    QuantifyReportDataPoint | CompareReportDataPoint,
    string,
    unknown
  >;
  shouldWrapLabel?: boolean;
  hasCoOccurringDataPointInData: boolean;
  labelType: LabelType;
}

export const QuantifyReportTableLabelCell = ({
  params,
  shouldWrapLabel = false, // default false because it only works when auto row height is enabled, which dsiables virtualization
  hasCoOccurringDataPointInData,
  labelType,
}: QuantifyReportTableLabelCellProps) => {
  const theme = useTheme();

  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const searchTerm = useQuantifyStore(({ visual }) => visual.searchTerm);

  const category = params.row.category;
  const shouldShowCoOccurringIcon = !params.row.isRelevant;
  const shouldShowCategory = !!category;

  const isInvisibleLabel = params.row.isInvisible;
  const isUpdateInProgressLabel = params.row.isUpdateInProgress;

  const groupByDisplayName = groupBy
    ? getDisplayNameFromField(getFieldNameFromEqlField(groupBy))
    : '';

  const getCoOccurringTooltipTitle = () => {
    return (
      <Typography variant="caption">
        Co-occurring {groupByDisplayName}.{' '}
        <Link
          href={QUANTIFY_COOCCURRING_HELP_DESK_ARTICLE}
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
          sx={{
            '&:hover': {
              color: 'inherit',
            },
          }}
        >
          Learn more <OpenInNewRounded fontSize="inherit" color="inherit" />
        </Link>
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        lineHeight: '16px',
        py: shouldWrapLabel ? 0.5 : 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box
          minWidth={hasCoOccurringDataPointInData ? COOCCURRENCE_ICON_SIZE : 0}
        >
          {shouldShowCoOccurringIcon && (
            <Tooltip title={getCoOccurringTooltipTitle()} arrow placement="top">
              <Box>
                <CooccurrenceIcon size={COOCCURRENCE_ICON_SIZE} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Tooltip
          arrow
          placement="top"
          title={
            isInvisibleLabel
              ? getInfoMessageForLabelState({
                  labelState: 'invisible',
                  labelType,
                })
              : null
          }
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              overflow: 'hidden',
            }}
          >
            {shouldShowCategory && (
              <Box>
                <CategoryIcon
                  category={category}
                  fill={
                    isInvisibleLabel ? theme.palette.text.disabled : undefined
                  }
                />
              </Box>
            )}
            <Box
              sx={{
                whiteSpace: shouldWrapLabel ? 'wrap' : 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: isInvisibleLabel ? 'text.disabled' : 'text.primary',
              }}
            >
              <Highlighter
                textToHighlight={params.value ?? ''}
                searchWords={[searchTerm]}
                autoEscape
                highlightTag={({ children }) => <strong>{children}</strong>}
              />
            </Box>
            {isUpdateInProgressLabel && (
              <Box>
                <LabelUpdateInProgressIcon size={20} labelType={labelType} />
              </Box>
            )}
          </Stack>
        </Tooltip>
      </Stack>
    </Box>
  );
};
